import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from './input';

/**
 * Combobox — type-to-filter selection from a list.
 *
 * For lists long enough that a Select becomes a scroll hunt. Shows the chosen
 * option's label when closed, and filters on both label and description as you
 * type, so a quote request can be found by its number or by the customer on it.
 *
 * options: [{ value, label, description }]
 */
function Combobox({
  value,
  onChange,
  options = [],
  placeholder = 'Search…',
  emptyText = 'No matches',
  disabled = false,
  allowClear = true,
  id,
  className
}) {
  const [term, setTerm] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  const selected = React.useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  React.useEffect(() => {
    const onDocumentClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setTerm('');
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const matches = React.useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) || (o.description || '').toLowerCase().includes(needle)
    );
  }, [options, term]);

  const choose = (option) => {
    onChange?.(option.value);
    setTerm('');
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <div className="relative">
        <Input
          id={id}
          disabled={disabled}
          autoComplete="off"
          placeholder={selected ? selected.label : placeholder}
          value={open ? term : selected?.label || ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              setTerm('');
            }
            if (e.key === 'Enter' && matches.length === 1) {
              e.preventDefault();
              choose(matches[0]);
            }
          }}
          className={cn('pr-8', !selected && !open && 'text-muted-foreground')}
        />

        {selected && allowClear && !disabled ? (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => {
              onChange?.(null);
              setTerm('');
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 opacity-60 hover:bg-accent hover:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {matches.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">{emptyText}</p>
          ) : (
            matches.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => choose(option)}
                className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                  )}
                </span>
                {option.value === value && <Check className="h-4 w-4 shrink-0" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export { Combobox };
